<template>
  <div class="quota-application-form">
    <PlateTitle title="投保信息" />
    <a-form
      :label-col="{ span: 6 }"
      :wrapper-col="{ span: 12 }"
    >
      <a-form-item label="投标人名称">
        <span>{{ orderInfo.name }}</span>
      </a-form-item>
      <a-form-item label="投标人统一社会信用代码">
        <span>{{ orderInfo.creditCode }}</span>
      </a-form-item>
      <a-form-item label="标段名称">
        <span>{{ orderInfo.sectionName }}</span>
      </a-form-item>
      <a-form-item label="标段编号">
        <span>{{ orderInfo.sectionCode }}</span>
      </a-form-item>
      <a-form-item label="担保金额">
        <span>{{ orderInfo.sumAmount }} 元</span>
      </a-form-item>
      <a-form-item label="招标人名称">
        <span>{{ orderInfo.tendereeName }}</span>
      </a-form-item>
      <a-form-item label="招标人统一社会信用代码">
        <span>{{ orderInfo.tendereeCreditCode }}</span>
      </a-form-item>

      <a-form-item
        label="出函机构"
        required
      >
        <a-alert
          v-if="!visiblePartners.length"
          type="warning"
          show-icon
          message="当前渠道暂无可用出函机构，请联系平台管理员"
        />
        <div
          v-else
          class="partner-list"
        >
          <button
            v-for="partner in visiblePartners"
            :key="partner.partnerCode"
            type="button"
            :class="[
              'partner-card',
              form.partnerCode === partner.partnerCode ? 'selected' : '',
              isFixedPartner ? 'fixed' : '',
            ]"
            :disabled="isFixedPartner"
            @click="selectPartner(partner)"
          >
            <img
              v-if="partnerLogo(partner)"
              :src="partnerLogo(partner)"
              :alt="partner.partnerName"
            >
            <span>{{ partner.partnerName || partner.partnerLogoName }}</span>
            <small v-if="isFixedPartner">固定机构</small>
          </button>
        </div>
      </a-form-item>
      <a-form-item label="保费">
        <span class="premium">{{ orderInfo.sumPremium }} 元</span>
      </a-form-item>
      <a-form-item
        v-if="profile.periodEnabled && effectiveTimes.length"
        label="保函有效期"
        required
      >
        <a-select
          v-model="form.period"
          style="width: 280px"
        >
          <a-select-option
            v-for="item in effectiveTimes"
            :key="item.value"
            :value="item.value"
          >
            {{ item.label }}
          </a-select-option>
        </a-select>
      </a-form-item>

      <template v-if="accountPolicy.visible">
        <a-form-item
          label="基本户户名"
          :required="accountPolicy.required"
        >
          <a-input
            v-model.trim="form.basicAccountName"
            placeholder="请输入基本户户名"
            style="width: 280px"
          />
        </a-form-item>
        <a-form-item
          label="基本户银行账号"
          :required="accountPolicy.required"
        >
          <a-input
            v-model.trim="form.basicAccountNo"
            placeholder="请输入基本户银行账号"
            style="width: 280px"
          />
          <div
            v-if="accountPolicy.showPayTip"
            class="basic-pay-tip"
          >
            本渠道要求使用所填基本户完成保费支付，请确认账号准确。
          </div>
        </a-form-item>
      </template>

      <a-form-item
        label="经办人"
        required
      >
        <a-input
          v-model.trim="form.responsiblePersonName"
          placeholder="请输入经办人姓名"
          style="width: 280px"
        />
      </a-form-item>
      <a-form-item
        label="经办人联系方式"
        required
      >
        <a-input
          v-model.trim="form.responsiblePersonPhone"
          maxlength="11"
          placeholder="请输入经办人手机号"
          style="width: 280px"
        />
      </a-form-item>
      <a-form-item
        v-if="profile.smsVerificationEnabled && profile.captchaEnabled"
        label="图形验证码"
        required
      >
        <div class="captcha-row">
          <a-input
            v-model.trim="form.captchaCode"
            maxlength="8"
            placeholder="请输入图形验证码"
            style="width: 180px"
          />
          <img
            v-if="captchaImg"
            :src="captchaImg"
            alt="图形验证码"
            title="点击刷新验证码"
            @click="refreshCaptcha"
          >
          <a
            v-else
            @click="refreshCaptcha"
          >刷新验证码</a>
        </div>
      </a-form-item>
      <a-form-item
        v-if="profile.smsVerificationEnabled"
        label="短信验证码"
        required
      >
        <div class="captcha-row">
          <a-input
            v-model.trim="form.phoneCode"
            maxlength="8"
            placeholder="请输入短信验证码"
            style="width: 180px"
          />
          <a-button
            :loading="smsSending"
            :disabled="smsCountdown > 0"
            @click="sendSms"
          >
            {{ smsCountdown > 0 ? `${smsCountdown}s后重试` : '获取验证码' }}
          </a-button>
        </div>
      </a-form-item>
    </a-form>
  </div>
</template>

<script>
import PlateTitle from '@/components/PlateTitle';
import { getCaptcha, sendPhoneCode } from '@/api/quota';

const {
  buildInformationPayload,
  getBasicAccountPolicy,
  getVisiblePartners,
  selectInitialPartner,
} = require('../policy');

export default {
  name: 'QuotaApplicationForm',
  components: { PlateTitle },
  props: {
    orderInfo: { type: Object, default: () => ({}) },
    profile: { type: Object, required: true },
    partners: { type: Array, default: () => [] },
    channelInfo: { type: Object, default: () => ({}) },
    lastAccount: { type: Object, default: () => ({}) },
    channelCode: { type: String, required: true },
    orderNo: { type: String, required: true },
  },
  data() {
    return {
      form: {
        partnerCode: '',
        period: '',
        basicAccountName: '',
        basicAccountNo: '',
        responsiblePersonName: '',
        responsiblePersonPhone: '',
        captchaCode: '',
        phoneCode: '',
      },
      captchaImg: '',
      captchaUuid: '',
      smsSending: false,
      smsCountdown: 0,
      smsTimer: null,
    };
  },
  computed: {
    accountPolicy() {
      return getBasicAccountPolicy(
        this.profile.basicAccountMode,
        this.profile.basicPayRequired
      );
    },
    isFixedPartner() {
      return this.profile.partnerMode === 'FIXED';
    },
    visiblePartners() {
      return getVisiblePartners(this.partners, this.profile);
    },
    effectiveTimes() {
      return Array.isArray(this.channelInfo.effectiveTime)
        ? this.channelInfo.effectiveTime
        : [];
    },
  },
  watch: {
    orderInfo: {
      immediate: true,
      deep: true,
      handler() { this.hydrate(); },
    },
    partners: {
      immediate: true,
      deep: true,
      handler() { this.hydratePartner(); },
    },
    lastAccount: {
      immediate: true,
      deep: true,
      handler() { this.hydrateLastAccount(); },
    },
    effectiveTimes: {
      immediate: true,
      handler(list) {
        if (!this.form.period && list.length) this.form.period = list[0].value;
      },
    },
  },
  mounted() {
    if (this.profile.smsVerificationEnabled && this.profile.captchaEnabled) {
      this.refreshCaptcha();
    }
  },
  beforeDestroy() {
    this.clearSmsTimer();
  },
  methods: {
    unwrap(response) {
      const body = response && response.data;
      return body && body.data !== undefined ? body.data : (body || {});
    },
    hydrate() {
      this.form.period = this.orderInfo.period || this.form.period;
      this.form.basicAccountName = this.orderInfo.basicAccountName || this.form.basicAccountName;
      this.form.basicAccountNo = this.orderInfo.basicAccountNo || this.form.basicAccountNo;
      this.form.responsiblePersonName = this.orderInfo.responsiblePersonName ||
        this.orderInfo.principalName || this.form.responsiblePersonName;
      this.form.responsiblePersonPhone = this.orderInfo.responsiblePersonPhone ||
        this.orderInfo.mobile || this.form.responsiblePersonPhone;
      this.hydratePartner();
    },
    hydratePartner() {
      const partner = selectInitialPartner(
        this.visiblePartners,
        this.profile,
        this.orderInfo.partnerCode || this.form.partnerCode
      );
      this.form.partnerCode = partner ? partner.partnerCode : '';
    },
    hydrateLastAccount() {
      const account = this.lastAccount || {};
      const accountNo = account.accountNo || account.basicAccountNo || account.data;
      if (!this.form.basicAccountNo && accountNo) this.form.basicAccountNo = accountNo;
      if (!this.form.basicAccountName && account.accountName) {
        this.form.basicAccountName = account.accountName;
      }
    },
    partnerLogo(partner) {
      if (partner.logoPath) return partner.logoPath;
      const logoBase64 = partner.partnerLogoPathBase64 || partner.logoPathBase64;
      if (logoBase64) {
        return `data:image/png;base64,${logoBase64}`;
      }
      return '';
    },
    selectPartner(partner) {
      if (!this.isFixedPartner) this.form.partnerCode = partner.partnerCode;
    },
    async refreshCaptcha() {
      try {
        const captcha = this.unwrap(await getCaptcha(this.channelCode, this.orderNo));
        this.captchaImg = captcha.img || '';
        this.captchaUuid = captcha.uuid || '';
        this.form.captchaCode = '';
        if (!this.captchaImg || !this.captchaUuid) throw new Error('图形验证码数据不完整');
      } catch (error) {
        this.captchaImg = '';
        this.captchaUuid = '';
        this.$notification.error({ message: error.message || '图形验证码加载失败' });
      }
    },
    async sendSms() {
      if (!/^1[3-9]\d{9}$/.test(this.form.responsiblePersonPhone || '')) {
        this.$message.warning('请先输入正确的经办人手机号');
        return;
      }
      if (this.profile.captchaEnabled && (!this.captchaUuid || !this.form.captchaCode)) {
        this.$message.warning('请先输入图形验证码');
        return;
      }
      this.smsSending = true;
      try {
        const payload = {
          orderNo: this.orderNo,
          phoneNum: this.form.responsiblePersonPhone,
        };
        if (this.profile.captchaEnabled) {
          payload.codeKey = this.captchaUuid;
          payload.code = this.form.captchaCode;
        }
        const response = await sendPhoneCode(this.channelCode, payload);
        const body = response && response.data || {};
        if (body.status !== undefined && Number(body.status) !== 200) {
          throw new Error(body.message || '短信验证码发送失败');
        }
        this.startSmsCountdown();
        this.$notification.success({ message: body.message || '短信验证码已发送' });
      } catch (error) {
        this.$notification.error({
          message: error && error.response && error.response.data && error.response.data.message ||
            error.message || '短信验证码发送失败',
        });
        if (this.profile.captchaEnabled) await this.refreshCaptcha();
      } finally {
        this.smsSending = false;
      }
    },
    startSmsCountdown() {
      this.clearSmsTimer();
      this.smsCountdown = 60;
      this.smsTimer = window.setInterval(() => {
        this.smsCountdown -= 1;
        if (this.smsCountdown <= 0) this.clearSmsTimer();
      }, 1000);
    },
    clearSmsTimer() {
      if (this.smsTimer) window.clearInterval(this.smsTimer);
      this.smsTimer = null;
      if (this.smsCountdown < 0) this.smsCountdown = 0;
    },
    validate() {
      if (!this.form.partnerCode) throw new Error('请选择出函机构');
      if (this.profile.periodEnabled && this.effectiveTimes.length && !this.form.period) {
        throw new Error('请选择保函有效期');
      }
      if (this.accountPolicy.required) {
        if (!this.form.basicAccountName) throw new Error('请输入基本户户名');
        if (!this.form.basicAccountNo) throw new Error('请输入基本户银行账号');
      }
      if (!this.form.responsiblePersonName) throw new Error('请输入经办人姓名');
      if (!/^1[3-9]\d{9}$/.test(this.form.responsiblePersonPhone || '')) {
        throw new Error('请输入正确的经办人手机号');
      }
      if (this.profile.smsVerificationEnabled && !this.form.phoneCode) {
        throw new Error('请输入短信验证码');
      }
      return true;
    },
    getPayload(orderNo) {
      this.validate();
      const payload = buildInformationPayload(
        { orderNo },
        this.profile,
        this.form
      );
      if (this.profile.periodEnabled && this.form.period) payload.period = this.form.period;
      return payload;
    },
  },
};
</script>

<style lang="less" scoped>
.quota-application-form {
  .premium { color: #d9001b; font-size: 22px; }
  .partner-list { display: flex; flex-wrap: wrap; gap: 16px; }
  .partner-card {
    width: 190px;
    min-height: 96px;
    padding: 10px;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    background: #fff;
    cursor: pointer;
    img { display: block; max-width: 160px; height: 48px; margin: 0 auto 6px; object-fit: contain; }
    span, small { display: block; }
    small { margin-top: 4px; color: #999; }
    &.selected { border: 2px solid #1890ff; }
    &.fixed { cursor: default; }
  }
  .basic-pay-tip { color: #d46b08; line-height: 1.5; }
  .captcha-row {
    display: flex;
    align-items: center;
    gap: 10px;
    img { width: 90px; height: 34px; cursor: pointer; object-fit: contain; }
  }
  /deep/ .ant-row { display: flex; margin-left: 12%; }
}
</style>
